import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import { useAuth } from '@/utils/Auth';
import { supabaseClient } from '@/utils/supabaseClient';
import { LanguageContext } from '../context/LanguageContext';
import { withSupabaseRetry } from '@/utils/retryUtils';
import dayjs from 'dayjs';

// Original user properties when in on-behalf mode
interface OriginalUserProps {
  name: string;
  departmentID: string;
  role: string;
}

// On-behalf user properties
interface OnBehalfUserProps {
  id: string;
  name: string;
  departmentID: string;
  companyID: string;
  email: string;
  position: string;
}

interface FirebaseContextProps {
  companyID: string;
  departmentID: string;
  groupId: string;
  companyName: string;
  town: string;
  allowances: any;
  leaveTypesData: any;
  setLeaveTypesData: React.Dispatch<React.SetStateAction<any>>;
  holidays: any;
  setHolidays: React.Dispatch<React.SetStateAction<any>>;
  holidaysLang: any;
  setHolidaysLang: React.Dispatch<React.SetStateAction<any>>;
  calendarStatus: CalendarStatusType;
  setCalendarStatus: React.Dispatch<React.SetStateAction<CalendarStatusType>>;
  company_departments: any;
  setCompany_departments: React.Dispatch<React.SetStateAction<any>>;
  //allDepartment: DepartmentData | null;
  // setAllDepartment: React.Dispatch<React.SetStateAction<DepartmentData | null>>;
  managers: any;
  name: any;
  setManagers: React.Dispatch<React.SetStateAction<any>>;
  allowanceCalendarStart: any;
  setAllowanceCalendarStart: React.Dispatch<React.SetStateAction<any>>;
  country: string;
  setCountry: React.Dispatch<React.SetStateAction<string>>;
  setAdditionalRow: React.Dispatch<React.SetStateAction<boolean>>;
  additionalRow: boolean;
  approversEmails: any;
  setApproversEmails: React.Dispatch<React.SetStateAction<any>>;
  workhours: string;
  setWorkhours: React.Dispatch<React.SetStateAction<string>>;
  startDate: string;
  role: string;
  position: string;
  setPosition: React.Dispatch<React.SetStateAction<string>>;
  codeOptions: Record<string, string>;
  setCodeOptions: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  codeColors: Record<string, string>;
  setCodeColors: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  first: boolean;
  setFirst: React.Dispatch<React.SetStateAction<boolean>>;
  eAllowancesInitial: any; // Adjust the type as needed
  setEAllowancesInitial: React.Dispatch<React.SetStateAction<any>>;
  company_work: boolean;
  setCompany_work: React.Dispatch<React.SetStateAction<boolean>>;
  // Active company properties
  originalCompanyID: string;
  originalCompanyName: string;
  setCompanyID: React.Dispatch<React.SetStateAction<string>>;
  setCompanyName: React.Dispatch<React.SetStateAction<string>>;
  changeActiveCompany: (newCompanyID: string, newCompanyName: string) => void;
  // New properties for on-behalf mode
  isOnBehalfMode?: boolean;
  originalUser?: OriginalUserProps;
  onBehalfUser?: OnBehalfUserProps;
  clearOnBehalfMode?: () => void;
  onBehalfData?: any; // Add onBehalfData property to store the entire on-behalf data
  setOnBehalfData?: React.Dispatch<React.SetStateAction<any>>;
  isChangingUser: boolean; // Add this property
  setIsChangingUser: React.Dispatch<React.SetStateAction<boolean>>; // Add this setter
  switchToOnBehalfUser?: (userData: any) => Promise<boolean>; // Add this new function to the interface
  handleFirstLogin: () => Promise<void>; // Add this new function to handle first login
  isLoading: boolean; // Add loading state to interface
}

interface CalendarStatusType {
  [state: string]: {
    [type: string]: {
      [date: string]: {
        hours: number;
        description?: string;
        unit?: number;
        array?: File[];
        gid?: string | number; // Changed to allow string values
        sign?: string | null;
        resign?: string | null;
        approvedDate?: string;
        requestDate?: string;
        replacement?: string;
        approvedBy?: string;
        assign?: string;
      };
    };
  };
}

// interface DepartmentData {
//   [key: string]: {
//     startDate: string;
//     CalendarStatus: CalendarStatusType;
//     email: string;
//     role: string;
//     name: string;
//     pNumber: string;
//     departmentID?: string;
//     companyID?: string;
//     companyName?: string;
//     town: string;
//     workhours: string;
//     position: string;
//     i?: any;
//     first?: boolean;
//   };
// }

interface Allowance {
  allowanceName: string;
  annualLeave: number | string;
  allowanceDescription: string;
  carryOver?: boolean;
  maxCarryOver?: number | string;
  carryOverExpires?: number;
  expiryUnit?: string;
  keep?: boolean;
  la?: boolean;
}

interface LeaveType {
  name: string;
  selectedallowance?: string;
  color?: string;
  description: string;
  type: string;
  code: string;
  workSchedule?: boolean;
  order_number: number;
  auto_approva: boolean;
  is_hidden: boolean;
}

interface WorkType {
  id: string;
  name: string;
  code: string;
  order_number: number;
}

interface Holiday {
  code: string;
  country: string;
  year: string | number;
  summary: string;
  start_date: string;
}

const FirebaseContext = createContext<FirebaseContextProps | undefined>(
  undefined
);

export const useFirebase = () => {
  const context = useContext(FirebaseContext);
  if (!context) {
    throw new Error('useFirebase must be used within a FirebaseProvider');
  }
  return context;
};

export const FirebaseProvider = ({
  children,
}: {
  children: React.ReactNode;
}): React.ReactNode => {
  const { user } = useAuth();
  const { language } = useContext(LanguageContext); // Initialize state from session storage with error handling

  // Add effect to clear on-behalf data on initial mount
  // useEffect(() => {
  //   console.log('Clearing on-behalf data on initial mount');
  //   sessionStorage.removeItem('authContextState_onBehalf');
  // }, []);
  const getSavedState = () => {
    try {
      // Clear saved state on initial load to avoid issues
      // Remove this line or comment it out if you want to preserve state between page refreshes
      sessionStorage.removeItem('authContextState');
      sessionStorage.removeItem('authContextState_onBehalf');
      console.log('Cleared saved state on initial load');

      // Check if we're acting on behalf of another user
      const onBehalfData = sessionStorage.getItem('authContextState_onBehalf');
      const authContextState =
        sessionStorage.getItem('authContextState') || '{}';

      // If we have on-behalf data, merge it with regular auth state
      if (onBehalfData) {
        const parsedOnBehalfData = JSON.parse(onBehalfData);
        const parsedAuthState = JSON.parse(authContextState);

        console.log(
          'Acting on behalf of user:',
          parsedOnBehalfData.selectedUser?.name,
          'ID:',
          parsedOnBehalfData.selectedUser?.id
        );

        // If we have full employee data from RPC, use it
        if (parsedOnBehalfData.fullData) {
          const { fullData } = parsedOnBehalfData;

          console.log('Using full data for on-behalf user');
          // Create a merged state with the on-behalf user data
          // Note: calendarStatus will be fetched fresh and kept in memory only
          return {
            ...parsedAuthState,
            // Override with on-behalf user data
            companyID: fullData.company_id || parsedAuthState.companyID,
            departmentID:
              fullData.department_id || parsedAuthState.departmentID,
            name: `${fullData.name || ''} ${fullData.last_name || ''}`.trim(),
            position: fullData.position || parsedAuthState.position,
            role: fullData.role || parsedAuthState.role,
            startDate: fullData.start_date || parsedAuthState.startDate,
            workhours:
              fullData.workhours?.toString() || parsedAuthState.workhours,
            first: fullData.first || parsedAuthState.first,
            // Use the allowances data from the on-behalf user
            eAllowancesInitial: parsedOnBehalfData.allowancesData || [],
            // calendarStatus will be fetched fresh - not loaded from storage
            // Add a flag to indicate we're using on-behalf data
            isOnBehalfMode: true,
            // Store the original user info in case we need it
            originalUser: {
              name: parsedAuthState.name,
              departmentID: parsedAuthState.departmentID,
              role: parsedAuthState.role,
            },
            // Store the full on-behalf data
            onBehalfData: parsedOnBehalfData,
            // Store the on-behalf user info
            onBehalfUser: parsedOnBehalfData.selectedUser,
          };
        }
        // If we don't have the full data yet, just use the basic selected user info
        if (parsedOnBehalfData.selectedUser) {
          console.log('Using basic data for on-behalf user');
          return {
            ...parsedAuthState,
            // Override with on-behalf user data we have
            name: parsedOnBehalfData.selectedUser.name,
            departmentID: parsedOnBehalfData.selectedUser.departmentID,
            companyID: parsedOnBehalfData.selectedUser.companyID,
            position: parsedOnBehalfData.selectedUser.position,
            // Use allowances data if available
            eAllowancesInitial: parsedOnBehalfData.allowancesData || [],
            // Add a flag to indicate we're using on-behalf data
            isOnBehalfMode: true,
            // Store the original user info in case we need it
            originalUser: {
              name: parsedAuthState.name,
              departmentID: parsedAuthState.departmentID,
              role: parsedAuthState.role,
            },
            // Store the full on-behalf data
            onBehalfData: parsedOnBehalfData,
            // Store the on-behalf user info
            onBehalfUser: parsedOnBehalfData.selectedUser,
          };
        }
      }

      return JSON.parse(authContextState);
    } catch (error) {
      console.error('Error parsing saved state:', error);
      return {};
    }
  };
  // Helper function to process calendar data into the expected format
  const processCalendarData = (calendarData: any) => {
    // Initialize a combined calendar status object
    const combinedCalendarStatus: CalendarStatusType = {};

    try {
      // Log useful debug information about the calendar data structure
      console.log(
        'Processing calendar data:',
        calendarData
          ? `Has calendar_status: ${!!calendarData.calendar_status}, ` +
              `Is array: ${Array.isArray(calendarData.calendar_status)}, ` +
              `Length: ${
                Array.isArray(calendarData.calendar_status)
                  ? calendarData.calendar_status.length
                  : 'N/A'
              }`
          : 'No calendar data'
      );

      // Process regular calendar status entries
      if (
        calendarData?.calendar_status &&
        Array.isArray(calendarData.calendar_status)
      ) {
        // Log structure of first item for debugging
        if (calendarData.calendar_status.length > 0) {
          console.log(
            'First calendar status entry example:',
            Object.keys(calendarData.calendar_status[0]).join(', ')
          );
        }

        const transformedCalendarStatus: CalendarStatusType =
          calendarData.calendar_status.reduce(
            (acc: CalendarStatusType, entry: any) => {
              // Skip invalid entries
              if (
                !entry.date_start ||
                !entry.date_end ||
                !entry.status ||
                !entry.type
              ) {
                console.warn(
                  'Skipping calendar entry with missing required fields:',
                  entry
                );
                return acc;
              }

              try {
                // Generate array of dates between start and end dates
                const dates: string[] = [];
                const startDate = new Date(entry.date_start);
                const endDate = new Date(entry.date_end); // Check for invalid dates
                if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
                  console.warn(
                    'Skipping calendar entry with invalid dates:',
                    entry.date_start,
                    entry.date_end
                  );
                  return acc;
                }

                // Include both start and end dates in the range
                for (
                  let date = new Date(startDate);
                  date <= endDate;
                  date.setDate(date.getDate() + 1)
                ) {
                  dates.push(dayjs(date).format('YYYY-MM-DD'));
                }

                // Helper function to safely parse attach field
                const parseAttach = (attachData: string | null): File[] => {
                  if (!attachData) return [];
                  if (attachData.startsWith('http'))
                    return [attachData as unknown as File];
                  try {
                    return JSON.parse(attachData);
                  } catch (e) {
                    console.warn('Failed to parse attach data:', e);
                    return [];
                  }
                };

                // For each date in the range, create an entry in the transformed structure
                dates.forEach((dateStr) => {
                  if (!acc[entry.status]) {
                    acc[entry.status] = {};
                  }
                  if (!acc[entry.status][entry.type]) {
                    acc[entry.status][entry.type] = {};
                  }

                  acc[entry.status][entry.type][dateStr] = {
                    hours: entry.hours,
                    description: entry.description || '',
                    unit: entry.unit,
                    array: parseAttach(entry.attach),
                    gid: entry.gid || undefined,
                    sign: entry.sign || null,
                    resign: entry.resign || null,
                    approvedDate: entry.approved_date
                      ? dayjs(entry.approved_date).format('YYYY-MM-DD HH:mm')
                      : undefined,
                    requestDate: entry.request_date
                      ? dayjs(entry.request_date).format('YYYY-MM-DD HH:mm')
                      : undefined,
                    replacement: entry.replacement || '',
                    approvedBy: entry.approved_by || undefined,
                  };
                });
              } catch (error) {
                console.error('Error processing calendar entry:', error, entry);
              }

              return acc;
            },
            {} as CalendarStatusType
          );

        // Merge into the combined status
        Object.assign(combinedCalendarStatus, transformedCalendarStatus);
      } else {
        console.log('No calendar_status data or not in expected format');
      }

      // Process work calendar entries if they exist
      if (
        calendarData?.calendar_work &&
        Array.isArray(calendarData.calendar_work) &&
        calendarData.calendar_work.length > 0
      ) {
        console.log(
          'Processing work calendar entries:',
          calendarData.calendar_work.length
        );

        const workCalendarStatus: CalendarStatusType =
          calendarData.calendar_work.reduce(
            (acc: CalendarStatusType, entry: any) => {
              // Initialize Work status if it doesn't exist
              if (!acc['Work']) {
                acc['Work'] = {};
              }

              // Ensure the type exists in the Work status
              if (!acc['Work'][entry.type]) {
                acc['Work'][entry.type] = {};
              }

              // Generate array of dates between start and end dates
              const dates: string[] = [];
              if (entry.date_start && entry.date_end) {
                const startDate = new Date(entry.date_start);
                const endDate = new Date(entry.date_end);

                // Include both start and end dates in the range
                for (
                  let date = new Date(startDate);
                  date <= endDate;
                  date.setDate(date.getDate() + 1)
                ) {
                  dates.push(dayjs(date).format('YYYY-MM-DD'));
                }

                // For each date in the range, create an entry
                dates.forEach((dateStr) => {
                  acc['Work'][entry.type][dateStr] = {
                    hours: entry.hours || 0,
                    description: entry.description || '',
                    gid: entry.gid || undefined, // Keep the original string value
                    assign: entry.assigned_by || '',
                  };
                });
              }

              return acc;
            },
            {} as CalendarStatusType
          );

        // Merge work calendar status into the combined status
        if (combinedCalendarStatus['Work']) {
          Object.keys(workCalendarStatus['Work'] || {}).forEach((type) => {
            if (!combinedCalendarStatus['Work'][type]) {
              combinedCalendarStatus['Work'][type] = {};
            }

            Object.assign(
              combinedCalendarStatus['Work'][type],
              workCalendarStatus['Work'][type]
            );
          });
        } else {
          combinedCalendarStatus['Work'] = workCalendarStatus['Work'] || {};
        }
      } else {
        console.log('No work calendar data to process');
      }

      return combinedCalendarStatus;
    } catch (error) {
      console.error('Error processing calendar data:', error);
      return {};
    }
  };

  const savedState = useMemo(getSavedState, []);

  // Loading states
  const [isLoadingUser, setIsLoadingUser] = useState(true);
  const [isLoadingCompany, setIsLoadingCompany] = useState(false); // Changed to false initially
  const [isLoadingDepartment, setIsLoadingDepartment] = useState(false); // Changed to false initially

  // State initialization with proper typing and default values
  const [first, setFirst] = useState<boolean>(savedState.first ?? false);
  const [codeOptions, setCodeOptions] = useState<Record<string, string>>(
    savedState.codeOptions ?? {}
  );
  const [codeColors, setCodeColors] = useState<Record<string, string>>(
    savedState.codeColors ?? {}
  );
  const [allowances, setAllowances] = useState<Allowance[]>(
    savedState.allowances ?? []
  );
  const [leaveTypesData, setLeaveTypesData] = useState<LeaveType[]>(
    savedState.leaveTypesData ?? []
  );
  const [workhours, setWorkhours] = useState<string>(
    savedState.workhours ?? ''
  );
  const [role, setRole] = useState<string>(savedState.role ?? '');
  const [startDate, setStartDate] = useState<string>(
    savedState.startDate ?? ''
  );
  const [companyID, setCompanyID] = useState<string>(
    savedState.companyID ?? ''
  );
  const [originalCompanyID, setOriginalCompanyID] = useState<string>(
    savedState.companyID ?? ''
  );
  const [originalCompanyName, setOriginalCompanyName] = useState<string>(
    savedState.companyName ?? ''
  );
  const [position, setPosition] = useState<string>(savedState.position ?? '');
  const [country, setCountry] = useState<string>(savedState.country ?? '');
  const [additionalRow, setAdditionalRow] = useState<boolean>(false);
  const [groupId, setGroupId] = useState<string>(savedState.groupId ?? '');
  const [eAllowancesInitial, setEAllowancesInitial] = useState<any>(
    savedState.eAllowancesInitial ?? []
  );
  const [allowanceCalendarStart, setAllowanceCalendarStart] = useState<
    string | null
  >(null);
  const [approversEmails, setApproversEmails] = useState<
    Record<string, Record<string, string>>
  >(savedState.approversEmails ?? {});
  const [departmentID, setDepartmentID] = useState<string>(
    savedState.departmentID ?? ''
  );
  const [managers, setManagers] = useState<any>(savedState.managers ?? null);
  const [companyName, setCompanyName] = useState<string>(
    savedState.companyName ?? ''
  );
  const [town, setTown] = useState<string>(savedState.town ?? '');
  const [name, setName] = useState<string>(savedState.name ?? ''); // Initialize calendarStatus with empty object instead of from saved state
  const [calendarStatus, setCalendarStatus] = useState<CalendarStatusType>(
    {} // Always start with empty object - will be populated by fetchCalendarData
  );
  const [holidays, setHolidays] = useState<any>(savedState.holidays ?? null);
  const [holidaysLang, setHolidaysLang] = useState<any>({});
  const [company_departments, setCompany_departments] = useState<any>(
    savedState.company_departments ?? null
  );

  const [company_work, setCompany_work] = useState<boolean>(false);

  // Add state for onBehalfData
  const [onBehalfData, setOnBehalfData] = useState<any>(
    savedState.onBehalfData ?? null
  );

  // Add state to track user switching
  const [isChangingUser, setIsChangingUser] = useState<boolean>(false);

  const fetchEmployeeData = useCallback(async () => {
    if (!user?.id) {
      setIsLoadingUser(false);
      return;
    }

    setIsLoadingCompany(true);
    setIsLoadingDepartment(true);

    try {
      const { data, error } = await withSupabaseRetry(
        async () =>
          await supabaseClient.rpc('get_employee_full_data', {
            p_user_id: user.id,
          })
      );

      if (error) throw error;
      console.log('Data from get_employee_full_data:', data);

      if (data) {
        // Set the original company ID and name when user data is first loaded
        if (data.company_id && data.company_name) {
          setOriginalCompanyID(data.company_id);
          setOriginalCompanyName(data.company_name);
        }

        // Fetch calendar data which now includes calendar-specific data
        fetchCalendarData();

        // Set employee data
        setCompanyID(data.company_id || '');
        setDepartmentID(data.department_id || '');
        setRole(data.role || '');
        setStartDate(data.start_date || '');
        setPosition(data.position || '');
        setName(`${data.name || ''} ${data.last_name || ''}`.trim());
        setWorkhours((data.workhours || '').toString());
        setFirst(!!data.first);

        // Set company_work value from the API response
        setCompany_work(!!data.company_work);

        // Set company data
        setGroupId(data.group_id || '');
        setCompanyName(data.company_name || '');
        setTown(data.company_town || '');
        setCountry(data.country || '');

        // Set department data with null safety
        const formattedDepartments = data.department_id
          ? {
              [data.department_id]: {
                name: data.department_name || '',
                deploc: data.deploc || '',
                id: data.department_id,
              },
            }
          : {};
        setCompany_departments((prevDepartments: any) => {
          // If prev departments exist and current role is Admin/Approver, keep existing departments
          if (
            (role === 'Admin' || role === 'Approver') &&
            Object.keys(prevDepartments || {}).length > 0
          ) {
            return prevDepartments;
          }

          // Otherwise, use the new formatted departments
          return formattedDepartments;
        });
      }
    } catch (error) {
      console.error('Error fetching employee data:', error);
    } finally {
      setIsLoadingUser(false);
      setIsLoadingCompany(false);
      setIsLoadingDepartment(false);
    }
  }, [user?.id]);

  // Helper function to format holidays
  const formatHolidays = (holidays: Holiday[] | null) => {
    if (!holidays || !Array.isArray(holidays)) {
      return {};
    }

    return holidays.reduce(
      (
        acc: {
          [code: string]: {
            [country: string]: {
              [year: string]: Array<{
                summary: string;
                startDate: string;
              }>;
            };
          };
        },
        holiday
      ) => {
        const { code, country, year, summary, start_date } = holiday;

        // Initialize nested objects if they don't exist
        if (!acc[code]) {
          acc[code] = {};
        }
        if (!acc[code][country]) {
          acc[code][country] = {};
        }
        if (!acc[code][country][year]) {
          acc[code][country][year] = [];
        }

        acc[code][country][year].push({
          summary,
          startDate: start_date,
        });

        return acc;
      },
      {}
    );
  };

  // Fetch user data
  //console.log('user', user);
  // Fetch employee allowances

  // Fetch holidays

  // Update holidays language
  const updateHolidaysLang = useCallback(
    (holidaysData: any) => {
      const langKey = language.toUpperCase();
      if (holidaysData[langKey]) {
        setHolidaysLang(holidaysData[langKey]);
      } else {
        const availableLangKey = Object.keys(holidaysData)[0];
        if (availableLangKey) {
          setHolidaysLang(holidaysData[availableLangKey]);
        } else if (holidaysData['EN']) {
          setHolidaysLang(holidaysData['EN']);
        } else {
          setHolidaysLang({});
        }
      }
    },
    [language]
  );
  const fetchCalendarData = useCallback(async () => {
    // If no user is logged in, return early
    if (!user?.id) return;

    // Check if we're in on-behalf mode and have the user ID from that
    const onBehalfData = sessionStorage.getItem('authContextState_onBehalf');
    let employeeId = user.id;

    // If we have on-behalf data, use the on-behalf user's ID instead
    if (onBehalfData) {
      try {
        const parsedData = JSON.parse(onBehalfData);
        if (parsedData.selectedUser?.id) {
          employeeId = parsedData.selectedUser.id;
          console.log(
            'Fetching calendar data for on-behalf user:',
            parsedData.selectedUser.name,
            'ID:',
            employeeId
          );
        }
      } catch (error) {
        console.error('Error parsing on-behalf data:', error);
      }
    }

    try {
      const { data: calendarData, error } = await withSupabaseRetry(
        async () =>
          await supabaseClient.rpc('get_employee_calendar_data', {
            p_employee_id: employeeId,
          })
      );

      if (error) throw error;

      console.log('Combined calendar data:', calendarData);
      if (calendarData) {
        // Handle allowance calendar start with null safety
        if (calendarData.allowance_calendar_start) {
          setAllowanceCalendarStart(
            calendarData.allowance_calendar_start.split('-').slice(1).join('-')
          );
        }
        console.log('Allowance calendar start:', allowanceCalendarStart);

        // Set code options and colors first
        const newCodes: { [key: string]: string } = {};
        const codeColors: { [key: string]: string } = {};

        // Process code options if available
        if (
          Array.isArray(calendarData.code_options) &&
          calendarData.code_options.length > 0
        ) {
          calendarData.code_options.forEach(
            (item: { code: string; code_name: string; color?: string }) => {
              newCodes[item.code] = item.code_name;
              codeColors[item.code] = item.color || '#CCCCCC'; // Default color if none provided
            }
          );
        }

        // Set the state for code options and colors
        setCodeOptions(newCodes);
        setCodeColors(codeColors);

        // Now process leave types with the colors we just extracted
        let combinedTypes: LeaveType[] = [];

        // Process leave types if available
        if (Array.isArray(calendarData.leave_types)) {
          const coloredLeaveTypes = calendarData.leave_types.map(
            (leaveType: LeaveType) => ({
              ...leaveType,
              color: codeColors[leaveType.code] || leaveType.color || '#CCCCCC', // Use code color or fallback to existing or default
            })
          );
          combinedTypes = [...coloredLeaveTypes];
        }

        // Process work types and add them to leave types
        if (Array.isArray(calendarData.work_types)) {
          const formattedWorkTypes = calendarData.work_types.map(
            (workType: WorkType) => ({
              name: workType.name,
              code: workType.code,
              color: codeColors[workType.code] || '#CCCCCC', // Use code color or default
              description: workType.name, // Using name as description
              type: 'Work', // Setting type to "Work" as requested
              workSchedule: true, // Setting workSchedule to true as requested
              order_number: workType.order_number,
              auto_approva: false, // Default value
              is_hidden: false, // Default value
            })
          );

          // Combine leave types and work types
          combinedTypes = [...combinedTypes, ...formattedWorkTypes];
        }

        // Sort by order_number
        combinedTypes.sort((a, b) => a.order_number - b.order_number);
        console.log('combinedTypes', combinedTypes);
        // Set combined types as leave types
        setLeaveTypesData(combinedTypes);

        // Set remaining calendar-specific data
        setAllowances(
          Array.isArray(calendarData.allowances) ? calendarData.allowances : []
        );
        setHolidays(
          calendarData.holidays ? formatHolidays(calendarData.holidays) : {}
        );

        // Process e_allowances data (includes generated fallback data from RPC if empty)
        if (
          calendarData.e_allowances &&
          Array.isArray(calendarData.e_allowances)
        ) {
          console.log('E-Allowances data received:', calendarData.e_allowances);
          setEAllowancesInitial(calendarData.e_allowances);
        } else {
          console.log('No e_allowances data received, setting empty array');
          setEAllowancesInitial([]);
        }

        // Initialize a combined calendar status object
        const combinedCalendarStatus: CalendarStatusType = {};

        // Process regular calendar status entries
        if (
          calendarData.calendar_status &&
          Array.isArray(calendarData.calendar_status)
        ) {
          const transformedCalendarStatus: CalendarStatusType =
            calendarData.calendar_status.reduce(
              (acc: CalendarStatusType, entry: any) => {
                // Generate array of dates between start and end dates
                const dates: string[] = [];
                const startDate = new Date(entry.date_start);
                const endDate = new Date(entry.date_end);

                // Include both start and end dates in the range
                for (
                  let date = new Date(startDate);
                  date <= endDate;
                  date.setDate(date.getDate() + 1)
                ) {
                  dates.push(dayjs(date).format('YYYY-MM-DD'));
                }

                // Helper function to safely parse attach field
                const parseAttach = (attachData: string | null): File[] => {
                  if (!attachData) return [];
                  if (attachData.startsWith('http'))
                    return [attachData as unknown as File];
                  try {
                    return JSON.parse(attachData);
                  } catch (e) {
                    console.warn('Failed to parse attach data:', e);
                    return [];
                  }
                };

                // For each date in the range, create an entry in the transformed structure
                dates.forEach((dateStr) => {
                  if (!acc[entry.status]) {
                    acc[entry.status] = {};
                  }
                  if (!acc[entry.status][entry.type]) {
                    acc[entry.status][entry.type] = {};
                  }
                  acc[entry.status][entry.type][dateStr] = {
                    hours: entry.hours,
                    description: entry.description || '',
                    unit: entry.unit,
                    array: parseAttach(entry.attach),
                    gid: entry.gid || undefined, // Keep the original string value
                    sign: entry.sign || null,
                    resign: entry.resign || null,
                    approvedDate: entry.approved_date
                      ? dayjs(entry.approved_date).format('YYYY-MM-DD HH:mm')
                      : undefined,
                    requestDate: entry.request_date
                      ? dayjs(entry.request_date).format('YYYY-MM-DD HH:mm')
                      : undefined,
                    replacement: entry.replacement || '',
                    approvedBy: entry.approved_by || undefined,
                  };
                });

                return acc;
              },
              {} as CalendarStatusType
            );

          // Merge into the combined status
          Object.assign(combinedCalendarStatus, transformedCalendarStatus);
        }

        // Process work calendar entries if they exist
        if (
          calendarData.calendar_work &&
          Array.isArray(calendarData.calendar_work) &&
          calendarData.calendar_work.length > 0
        ) {
          const workCalendarStatus: CalendarStatusType =
            calendarData.calendar_work.reduce(
              (acc: CalendarStatusType, entry: any) => {
                // Initialize Work status if it doesn't exist
                if (!acc['Work']) {
                  acc['Work'] = {};
                }

                // Ensure the type exists in the Work status
                if (!acc['Work'][entry.type]) {
                  acc['Work'][entry.type] = {};
                }

                // Generate array of dates between start and end dates
                const dates: string[] = [];
                if (entry.date_start && entry.date_end) {
                  const startDate = new Date(entry.date_start);
                  const endDate = new Date(entry.date_end);

                  // Include both start and end dates in the range
                  for (
                    let date = new Date(startDate);
                    date <= endDate;
                    date.setDate(date.getDate() + 1)
                  ) {
                    dates.push(dayjs(date).format('YYYY-MM-DD'));
                  }

                  // For each date in the range, create an entry
                  dates.forEach((dateStr) => {
                    acc['Work'][entry.type][dateStr] = {
                      hours: entry.hours || 0,
                      description: entry.description || '',
                      gid: entry.gid || undefined, // Keep the original string value
                      assign: entry.assigned_by || '',
                    };
                  });
                }

                return acc;
              },
              {} as CalendarStatusType
            );

          // Merge work calendar status into the combined status
          if (combinedCalendarStatus['Work']) {
            Object.keys(workCalendarStatus['Work'] || {}).forEach((type) => {
              if (!combinedCalendarStatus['Work'][type]) {
                combinedCalendarStatus['Work'][type] = {};
              }

              Object.assign(
                combinedCalendarStatus['Work'][type],
                workCalendarStatus['Work'][type]
              );
            });
          } else {
            combinedCalendarStatus['Work'] = workCalendarStatus['Work'] || {};
          }
        }

        console.log('Combined calendar status:', combinedCalendarStatus);
        setCalendarStatus(combinedCalendarStatus);
      }
    } catch (error) {
      console.error('Error fetching calendar data:', error);
    }
  }, [user?.id]);

  // Fetch approvers
  const fetchApprovers = useCallback(async () => {
    if (!user?.id || !role || (!departmentID && role === 'Employee')) return;
    console.log('role44', role);

    try {
      if (role === 'Employee') {
        const { data: departmentApprovers, error: approversError } =
          await supabaseClient
            .from('department_approvers')
            .select(
              `
            approver_id,
            employees:approver_id (
              id,
              name,
              last_name,
              email
            )
          `
            )
            .eq('department_id', departmentID);

        if (approversError) throw approversError;

        if (departmentApprovers) {
          const formattedApprovers: Record<string, Record<string, string>> = {};
          departmentApprovers.forEach((approver: any) => {
            if (approver.employees) {
              formattedApprovers[approver.approver_id] = {
                [`${approver.employees.name} ${approver.employees.last_name}`]:
                  approver.employees.email,
              };
            }
          });
          setApproversEmails(formattedApprovers);
        }
      } else if (role === 'Admin' || role === 'Approver') {
        const { data: managerData, error: managersError } = await supabaseClient
          .from('managers')
          .select(
            `
            manager_id,
            employees:manager_id (
              id,
              name,
              last_name,
              email
            )
          `
          )
          .eq('approver_id', user.id);

        if (managersError) throw managersError;
        console.log('managerData', managerData);

        if (managerData) {
          const formattedManagers: Record<string, Record<string, string>> = {};
          managerData.forEach((manager: any) => {
            if (manager.employees) {
              formattedManagers[manager.manager_id] = {
                [`${manager.employees.name} ${manager.employees.last_name}`]:
                  manager.employees.email,
              };
            }
          });
          setApproversEmails(formattedManagers);
          console.log('formattedManagers', formattedManagers);
        }

        console.log('approversEmails', approversEmails);
      }
    } catch (error) {
      console.error('Error fetching approvers:', error);
      setApproversEmails({});
    }
  }, [user?.id, role, departmentID]);

  // Effect to update leave types colors when codeColors change
  useEffect(() => {
    if (Object.keys(codeColors).length > 0 && leaveTypesData.length > 0) {
      setLeaveTypesData((prev) =>
        prev.map((leaveType) => ({
          ...leaveType,
          color:
            leaveType.code && codeColors[leaveType.code]
              ? codeColors[leaveType.code]
              : leaveType.color,
        }))
      );
    }
  }, [codeColors, leaveTypesData.length]);

  // Effect for user data
  useEffect(() => {
    fetchEmployeeData();
  }, [fetchEmployeeData]);
  // We no longer need a separate effect for employee allowances
  // as they are now fetched as part of the calendar data in fetchCalendarData

  // Effect for approvers
  useEffect(() => {
    if (
      user?.id &&
      role &&
      (departmentID || role === 'Admin' || role === 'Approver')
    ) {
      fetchApprovers();
    }
  }, [user?.id, role, departmentID, fetchApprovers]);

  // Effect for department data
  // useEffect(() => {
  //   handleDepartmentData();
  // }, [handleDepartmentData]);

  // Effect for holidays language update
  useEffect(() => {
    if (holidays) {
      updateHolidaysLang(holidays);
    }
  }, [holidays, updateHolidaysLang]);

  const fetchCompanyDepartments = useCallback(async () => {
    if (!companyID || !role || (role !== 'Admin' && role !== 'Approver'))
      return;

    try {
      const { data, error } = await supabaseClient
        .from('company_departments')
        .select('id, department_name, department_location, deploc')
        .eq('company_id', companyID);

      if (error) throw error;

      setCompany_departments((prevDepartments: any) => {
        const formattedDepartments = data.reduce((acc, dept) => {
          acc[dept.id] = {
            name: dept.department_name,
            deploc:
              dept.deploc ||
              `${dept.department_name} - ${dept.department_location}`,
            id: dept.id,
          };
          return acc;
        }, {} as Record<string, any>);

        // Merge with existing departments instead of replacing
        return {
          ...prevDepartments,
          ...formattedDepartments,
        };
      });
    } catch (error) {
      console.error('Error fetching company departments:', error);
    }
  }, [companyID, role]);

  useEffect(() => {
    if (role && (role === 'Admin' || role === 'Approver')) {
      fetchCompanyDepartments();
    }
  }, [role, fetchCompanyDepartments]);
  // Save state to session storage with debounce (excluding calendarStatus)
  useEffect(() => {
    const debounceTimeout = setTimeout(() => {
      try {
        const stateToSave = {
          managers,
          name,
          allowances,
          leaveTypesData,
          companyID,
          departmentID,
          companyName,
          town,
          // calendarStatus - excluded from session storage
          holidays,
          company_departments,
          //  allDepartment,
          groupId,
          approversEmails,
          workhours,
          role,
          startDate,
          eAllowancesInitial,
          position,
          codeOptions,
          codeColors,
          first,
          company_work,
        };

        sessionStorage.setItem('authContextState', JSON.stringify(stateToSave));
      } catch (error) {
        console.error('Error saving state to session storage:', error);
      }
    }, 1000); // 1 second debounce

    return () => clearTimeout(debounceTimeout);
  }, [
    managers,
    name,
    allowances,
    leaveTypesData,
    companyID,
    departmentID,
    companyName,
    town,
    // calendarStatus - removed from dependency array
    holidays,
    company_departments,
    // allDepartment,
    groupId,
    approversEmails,
    workhours,
    role,
    startDate,
    eAllowancesInitial,
    position,
    codeOptions,
    codeColors,
    first,
    company_work,
  ]);

  const isLoading = useMemo(() => {
    // If there's no user, don't show loading state
    if (!user) {
      return false;
    }

    // If there is a user, check all loading states
    return isLoadingUser || isLoadingCompany || isLoadingDepartment;
  }, [user, isLoadingUser, isLoadingCompany, isLoadingDepartment]); // Components that need to check loading state can do so through the context:
  // const { isLoading } = useFirebase();
  // if (isLoading) {
  //   return <div>Loading...</div>;
  // }

  const isOnBehalfMode = savedState.isOnBehalfMode || false;
  const originalUser = savedState.originalUser || null;
  const onBehalfUser = savedState.onBehalfUser || null;
  // Check if we're in on-behalf mode from saved state

  // Function to fix the context state in case of issues - this will run once per context initialization
  useEffect(() => {
    if (isOnBehalfMode && savedState.onBehalfUser && user?.id) {
      console.log(
        'Context initialized in on-behalf mode for user:',
        savedState.onBehalfUser.name
      );
    }
  }, [isOnBehalfMode, savedState.onBehalfUser, user?.id]);

  // Function to clear on-behalf mode and reload original user data
  const clearOnBehalfMode = useCallback(async () => {
    console.log('Clearing on-behalf mode without page refresh');
    setIsChangingUser(true);

    try {
      // Clean up session storage
      let cleanState = {};
      const currentAuthState = sessionStorage.getItem('authContextState');
      if (currentAuthState) {
        const parsedState = JSON.parse(currentAuthState);
        const {
          isOnBehalfMode,
          originalUser,
          onBehalfUser,
          onBehalfData,
          // calendarStatus removed - not stored in session storage anymore
          eAllowancesInitial,
          ...restState
        } = parsedState;

        cleanState = restState;
        sessionStorage.removeItem('authContextState_onBehalf');
        sessionStorage.setItem('authContextState', JSON.stringify(cleanState));

        // Reset in-memory states
        setOnBehalfData(null);
        setCalendarStatus({});
        setEAllowancesInitial([]);

        // Restore original user data
        if (originalUser) {
          setName(originalUser.name);
          setDepartmentID(originalUser.departmentID);
          setRole(originalUser.role);
        }

        // Wait for state updates to be processed
        // await new Promise((resolve) => requestAnimationFrame(resolve));        // Fetch data for original user
        try {
          // We only need to call fetchEmployeeData now since it includes calendar data with allowances
          await fetchEmployeeData();
        } catch (fetchError) {
          console.error('Error fetching original user data:', fetchError);
        }
      }
    } catch (error) {
      console.error('Error updating auth state during cleanup:', error);
    } finally {
      setIsChangingUser(false);
    }
  }, [fetchEmployeeData]);

  // Add a new function to switch to a user in on-behalf mode without page reload
  const switchToOnBehalfUser = useCallback(
    async (userData: any): Promise<boolean> => {
      if (!userData || !userData.selectedUser) {
        console.error('Invalid user data for switching');
        return false;
      }

      setIsChangingUser(true);

      try {
        const { fullData, calendarData, allowancesData, selectedUser } =
          userData;

        // Session storage updates
        const onBehalfData = {
          selectedUser,
          switchTimestamp: Date.now(),
        };

        sessionStorage.setItem(
          'authContextState_onBehalf',
          JSON.stringify(onBehalfData)
        );

        // Auth context state update
        const currentAuthState = sessionStorage.getItem('authContextState');
        if (currentAuthState) {
          const parsedState = JSON.parse(currentAuthState);
          const updatedState = {
            ...parsedState,
            isOnBehalfMode: true,
            originalUser: { name, departmentID, role },
            onBehalfUser: selectedUser,
          };
          sessionStorage.setItem(
            'authContextState',
            JSON.stringify(updatedState)
          );
        }

        // Update in-memory state
        setOnBehalfData({ selectedUser });

        // Update UI-relevant parts
        if (fullData) {
          setCompanyID(fullData.company_id || companyID);
          setDepartmentID(fullData.department_id || departmentID);
          setName(
            `${fullData.name || ''} ${fullData.last_name || ''}`.trim() ||
              selectedUser.name
          );
          setPosition(fullData.position || selectedUser.position);
          setRole(fullData.role || role);
          setWorkhours((fullData.workhours?.toString() || '').toString());
          setStartDate(fullData.start_date || startDate);
        } else {
          setName(selectedUser.name);
          setDepartmentID(selectedUser.departmentID);
          setCompanyID(selectedUser.companyID);
          setPosition(selectedUser.position);
        } // Process calendar data asynchronously if available
        if (calendarData) {
          const processedCalendarData = processCalendarData(calendarData);
          setCalendarStatus(processedCalendarData || {});
        } else {
          // Reset calendar status and fetch fresh data
          setCalendarStatus({});
          // The fetchCalendarData will be called by fetchEmployeeData
        }

        // Set allowances data if available
        if (allowancesData && Array.isArray(allowancesData)) {
          setEAllowancesInitial(allowancesData);
        } else {
          setEAllowancesInitial([]);
        }

        // Wait for state updates to be processed
        // await new Promise((resolve: (value: unknown) => void) =>
        //   requestAnimationFrame(resolve)
        // );

        // Finalize the switch
        setIsChangingUser(false);
        return true;
      } catch (error) {
        console.error('Error switching to on-behalf user:', error);
        setIsChangingUser(false);
        return false;
      }
    },
    [
      name,
      departmentID,
      role,
      companyID,
      position,
      startDate,
      processCalendarData,
    ]
  );

  // Function to switch to a different company
  const changeActiveCompany = useCallback(
    async (newCompanyID: string, newCompanyName: string) => {
      console.log(
        `Changing active company to: ${newCompanyName} (${newCompanyID})`
      );

      // Store new company info
      setCompanyID(newCompanyID);
      setCompanyName(newCompanyName);
      setIsChangingUser(true);

      try {
        // Fetch company-specific data like departments, allowances, etc.
        if (role === 'Admin' || role === 'Approver') {
          // Fetch company departments for the new company
          const { data: deptData, error: deptError } = await supabaseClient
            .from('company_departments')
            .select('*')
            .eq('company_id', newCompanyID);

          if (!deptError && deptData) {
            setCompany_departments(deptData);
          }

          // Fetch calendar data, leave types, etc. for the new company
          // You could also call fetchCalendarData() here if needed
        }

        // Other data that might need to be updated for the new company
        setIsChangingUser(false);
        return true;
      } catch (error) {
        console.error('Error changing active company:', error);
        setIsChangingUser(false);
        return false;
      }
    },
    [role]
  );

  // Function to handle first login completion
  const handleFirstLogin = useCallback(async () => {
    if (!user?.id || !first) return;

    try {
      console.log('Updating first login status for user:', user.id);

      const { error } = await supabaseClient
        .from('employees')
        .update({ first: false })
        .eq('id', user.id);

      if (error) {
        console.error('Error updating first login status:', error);
        throw error;
      }

      console.log('Successfully updated first login status');

      // Update local state
      setFirst(false);
    } catch (error) {
      console.error('Failed to update first login status:', error);
    }
  }, [user?.id, first]);

  // Memoize context value
  const firebaseContextValue = useMemo(
    () => ({
      managers: user ? managers : null,
      name: user ? name : '',
      setManagers,
      companyID: user ? companyID : '',
      setCompanyID,
      companyName: user ? companyName : '',
      setCompanyName,
      originalCompanyID: user ? originalCompanyID : '',
      originalCompanyName: user ? originalCompanyName : '',
      changeActiveCompany,
      town: user ? town : '',
      departmentID: user ? departmentID : '',
      groupId: user ? groupId : '',
      allowances: user ? allowances : [],
      leaveTypesData: user ? leaveTypesData : [],
      setLeaveTypesData,
      country: user ? country : '',
      setCountry,
      holidays: user ? holidays : null,
      setHolidays,
      holidaysLang: user ? holidaysLang : {},
      setHolidaysLang,
      calendarStatus: user ? calendarStatus : {},
      setCalendarStatus,
      company_departments: user ? company_departments : null,
      setCompany_departments,
      // allDepartment: user ? allDepartment : null,
      //  setAllDepartment,
      allowanceCalendarStart: user ? allowanceCalendarStart : null,
      setAllowanceCalendarStart,
      additionalRow: user ? additionalRow : false,
      setAdditionalRow,
      approversEmails: user ? approversEmails : {},
      setApproversEmails,
      workhours: user ? workhours : '',
      setWorkhours,
      startDate: user ? startDate : '',
      role: user ? role : '',
      position: user ? position : '',
      setPosition,
      codeOptions: user ? codeOptions : {},
      setCodeOptions,
      codeColors: user ? codeColors : {},
      setCodeColors,
      first: user ? first : false,
      setFirst,
      eAllowancesInitial: user ? eAllowancesInitial : null,
      setEAllowancesInitial,
      isLoading,
      company_work: user ? company_work : false,
      setCompany_work,
      // Add on-behalf mode properties
      isOnBehalfMode: user ? isOnBehalfMode : false,
      originalUser: user ? originalUser : null,
      onBehalfUser: user ? onBehalfUser : null,
      onBehalfData: user ? onBehalfData : null,
      setOnBehalfData,
      clearOnBehalfMode,
      isChangingUser,
      setIsChangingUser,
      switchToOnBehalfUser,
      handleFirstLogin,
    }),
    [
      user,
      managers,
      name,
      companyID,
      companyName,
      originalCompanyID,
      originalCompanyName,
      changeActiveCompany,
      town,
      departmentID,
      groupId,
      allowances,
      leaveTypesData,
      country,
      holidays,
      holidaysLang,
      calendarStatus,
      company_departments,
      // allDepartment,
      allowanceCalendarStart,
      additionalRow,
      approversEmails,
      workhours,
      startDate,
      role,
      position,
      codeOptions,
      codeColors,
      first,
      eAllowancesInitial,
      isLoading,
      company_work,
      // Add new dependencies
      isOnBehalfMode,
      originalUser,
      onBehalfUser,
      onBehalfData,
      clearOnBehalfMode,
      switchToOnBehalfUser, // Add the new dependency
      handleFirstLogin,
    ]
  );

  return (
    <FirebaseContext.Provider value={firebaseContextValue}>
      {children}
    </FirebaseContext.Provider>
  );
};
